<template>
  <!--
    Stats trigger wraps the 5 role/total chips. Click anywhere on this
    block (or press Enter / Space when focused) to toggle the popup.
    Clicking outside dismisses it. The popup is anchored to the right
    edge of the block (`right-0` of this relative container), so it stays
    under the TOTAL chip's column.
    Defence-in-depth; the canonical safelist lives in `src/styles/tailwind.css`
    via `@source inline(...)`. The companion safelist test
    (`src/styles/tailwind.safelist.test.ts`) pins the rule down so a
    refactor cannot silently drop these classes from the built CSS.
    Sentinel: bg-role-tank bg-role-dps bg-role-heal text-role-tank text-role-dps text-role-heal
  -->
  <div
    ref="statsTrigger"
    role="button"
    tabindex="0"
    aria-haspopup="dialog"
    :aria-expanded="statsPopupOpen"
    class="relative inline-flex items-center gap-3 px-2 py-1 rounded-md cursor-pointer border border-transparent outline-none transition-[background-color,border-color] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-bg-elevated/50 hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent aria-expanded:bg-bg-elevated/50 aria-expanded:border-border-strong"
    :aria-label="stats.triggerLabel(room.name)"
    @click="onStatsChipClick"
    @keydown.enter.prevent="onStatsChipClick"
    @keydown.space.prevent="onStatsChipClick"
  >
    <div class="flex flex-col items-center">
      <span
        class="font-display text-base font-semibold leading-none"
        :style="{ color: roleColors.forRole(WeaponRole.Tank).borderVar }"
      >
        {{ roleCounts.get(PlayerRole.Tank) }}
      </span>
      <span class="mt-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
        {{ messages.roles.weapon[WeaponRole.Tank] }}
      </span>
    </div>
    <div class="flex flex-col items-center">
      <span
        class="font-display text-base font-semibold leading-none"
        :style="{ color: roleColors.forRole(WeaponRole.Dps).borderVar }"
      >
        {{ roleCounts.get(PlayerRole.Dps) }}
      </span>
      <span class="mt-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
        {{ messages.roles.weapon[WeaponRole.Dps] }}
      </span>
    </div>
    <div class="flex flex-col items-center">
      <span class="font-display text-base font-semibold leading-none text-text-secondary">
        {{ roleCounts.get(PlayerRole.Hybrid) }}
      </span>
      <span class="mt-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
        {{ messages.roles.player[PlayerRole.Hybrid] }}
      </span>
    </div>
    <div class="flex flex-col items-center">
      <span
        class="font-display text-base font-semibold leading-none"
        :style="{ color: roleColors.forRole(WeaponRole.Heal).borderVar }"
      >
        {{ roleCounts.get(PlayerRole.Heal) }}
      </span>
      <span class="mt-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
        {{ messages.roles.weapon[WeaponRole.Heal] }}
      </span>
    </div>
    <div class="flex flex-col items-center">
      <span class="font-display text-base font-semibold leading-none text-accent">
        <template v-if="showsUnlimitedTotal">{{ assigned }} / <span :aria-label="stats.unlimited">∞</span></template>
        <template v-else>{{ assigned }} / {{ totalSlots }}</template>
      </span>
      <span class="mt-0.5 font-mono text-xs uppercase tracking-widest text-text-muted">
        {{ messages.roles.total }}
      </span>
    </div>

    <!--
      Weapon stats popup — absolute, anchored to the right edge of the
      trigger (the TOTAL column). Opens on click of the trigger and
      dismisses on any outside mousedown. Two stacked sections: a 3-tile
      role summary on top, then a horizontal bar chart of weapon counts.
    -->
    <div
      v-if="statsPopupOpen"
      ref="statsPopup"
      class="absolute top-full right-0 mt-2 z-50 min-w-(--min-w-popup-stats) max-w-(--max-w-popup-stats) bg-bg-elevated border border-border-strong rounded-lg shadow-xl p-4 flex flex-col gap-3"
      role="dialog"
      :aria-label="stats.dialogLabel"
      tabindex="-1"
    >
      <header class="flex items-center justify-between gap-2">
        <span class="font-display text-xs font-semibold tracking-wide uppercase text-text-muted">
          {{ stats.dialogLabel }}
        </span>
        <button
          type="button"
          class="inline-flex items-center justify-center w-6 h-6 text-base leading-none text-text-secondary bg-transparent border border-transparent rounded-sm cursor-pointer hover:bg-bg-surface hover:text-text-primary hover:border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :aria-label="stats.closeLabel"
          @click="closeStatsPopup"
        >×</button>
      </header>
      <!-- Section 1: role summary — three big role tiles. -->
      <section class="flex items-stretch gap-2">
        <div
          v-for="role in statsRoleTiles"
          :key="role.role"
          class="flex-1 flex flex-col items-center gap-0.5 px-2 py-2 rounded-md bg-bg-surface border border-border"
        >
          <span class="font-mono text-xs uppercase tracking-widest text-text-muted">
            {{ role.label }}
          </span>
          <span
            class="font-display text-3xl font-bold leading-none"
            :class="role.textClass"
          >
            {{ roleCounts.get(role.role) }}
          </span>
          <span class="font-mono text-xs uppercase tracking-widest text-text-muted">
            {{ stats.players }}
          </span>
        </div>
      </section>

      <!-- Section 2: weapon bar chart. -->
      <section v-if="sortedStatsRows.length > 0" class="flex flex-col gap-1.5">
        <header class="flex items-center justify-between">
          <span class="font-display text-xs font-semibold tracking-wide uppercase text-text-muted">
            {{ stats.loadout }}
          </span>
          <span class="font-mono text-xs uppercase tracking-widest text-text-muted">
            {{ stats.totalCount(statsTotal) }}
          </span>
        </header>
        <ul class="flex flex-col gap-2">
          <li
            v-for="row in sortedStatsRows"
            :key="row.slug"
            class="flex flex-col gap-1"
          >
            <div class="flex items-center gap-2">
              <img
                v-if="row.icon"
                :src="row.icon"
                :alt="row.name"
                class="w-6 h-6 object-cover rounded-sm border border-border-strong shrink-0"
                draggable="false"
              />
              <span
                v-else
                class="w-6 h-6 inline-flex items-center justify-center font-mono text-xs font-bold text-text-muted border border-dashed border-border-strong rounded-sm shrink-0"
                aria-hidden="true"
              >+</span>
              <span class="flex-1 min-w-0 text-sm text-text-primary whitespace-nowrap">{{ localizedRowName(row) }}</span>
              <span class="font-mono text-sm font-semibold text-text-primary shrink-0">
                ×{{ row.count }}
              </span>
            </div>
            <div class="h-1.5 bg-bg-surface rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-[width] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)]"
                :class="row.barClass"
                :style="{ width: row.barWidth + '%' }"
                role="presentation"
              />
            </div>
          </li>
        </ul>
      </section>
      <p v-else class="py-2 text-sm text-text-muted italic text-center">
        {{ stats.noWeaponsAssigned }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
// Stateful component. This is one of the few exceptions to the
// "stateless-by-default" rule for GvG planner components: the trigger
// chip + popup pair owns its own open flag, focus trap, and outside-click
// dismissal, all of which belong to a single popup lifecycle. Hoisting
// this state into `RoomSection` (or via `v-model:open`) would force a
// re-render of the entire room on every popup toggle, plus the focus-trap
// setup/teardown needs to fire on `nextTick` after the popup mounts — that
// wiring is local to the popup DOM and would not survive being lifted.
// Same justification as `GvgPlannerView`, `WeaponPicker`, and the
// `PickerController` for the builds popup in `PlayerCard`.

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'
import type { Player, Room } from '../../types'
import { PlayerRole, WeaponRole } from '@/types/weapon'
import { useFocusTrap } from '@/shared/composables'
import { useRoleColors } from '@/features/gvg-planner/composables'
import { weaponDisplayName } from '../../lib/roleLabels'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'

export interface WeaponStatsRoleTile {
  readonly role: PlayerRole
  readonly label: string
  readonly textClass: string
  readonly barClass: string
}

export interface WeaponStatsRow {
  readonly slug: string
  readonly name: string
  readonly icon: string
  readonly count: number
  readonly barClass: string
  readonly barWidth: number
}

export interface WeaponStatsPopupProps {
  readonly room: Room
  readonly roleCounts: ReadonlyMap<PlayerRole, number>
  readonly sortedStatsRows: ReadonlyArray<WeaponStatsRow>
  readonly statsRoleTiles: ReadonlyArray<WeaponStatsRoleTile>
  readonly statsTotal: number
  /**
   * Two-way bound via `v-model:open`. Parent owns the "open" intent (e.g.
   * could be wired up to a future keyboard shortcut or query param) but
   * defaults to `false` so the popup stays closed until the user clicks
   * the trigger chip.
   */
  readonly open: boolean
}

export interface WeaponStatsPopupEmits {
  (e: 'update:open', value: boolean): void
}

const props = defineProps<WeaponStatsPopupProps>()
const emit = defineEmits<WeaponStatsPopupEmits>()

const store = useGvgPlannerStore()
const roleColors = useRoleColors()
const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)
const stats = computed(() => messages.value.stats)

// ----- trigger / popup refs -----
// `statsTrigger` is the clickable chips div; `statsPopup` is the absolute
// dialog panel that anchors to its right edge. Both refs are local — they
// don't survive being lifted to `RoomSection` (the focus trap reads them
// per toggle) and they only exist while this component is mounted.
const statsTrigger = ref<HTMLDivElement | null>(null)
const statsPopup = ref<HTMLDivElement | null>(null)

// The open flag is two-way bound via `v-model:open` so the parent can
// observe toggles without forcing the popup to be a controlled component.
// Internal mirrors live here; `watch(statsPopupOpen)` keeps focus /
// keydown listener state in sync with the DOM.
const statsPopupOpen = computed<boolean>({
  get: () => props.open,
  set: (value) => emit('update:open', value),
})

const { activate: activateStatsTrap, deactivate: deactivateStatsTrap } = useFocusTrap(statsPopup)

// Per-room roster walk for the TOTAL chip. Mirrors what `RoomSection` does
// for the section-level role counts; the popup needs the same numbers next
// to the role chips inside the trigger.
const groupIdSet = computed<ReadonlySet<string>>(
  () => new Set(store.roomGroups(props.room.id).map((g) => g.id)),
)

const playersInRoom = computed<ReadonlyArray<Player>>(() =>
  store.currentPlayers.filter((p) => p.group !== null && groupIdSet.value.has(p.group)),
)

const totalSlots = computed<number>(() =>
  store.roomGroups(props.room.id).reduce((sum, g) => sum + g.maxPlayers, 0),
)

const assigned = computed<number>(() => playersInRoom.value.length)

// For `noPlayerLimit` rooms (Pool), the per-group cap pill renders "∞"
// so the room-level TOTAL should match: show `assigned / ∞` instead of
// an arbitrary sum of group `maxPlayers`. `props.room.noPlayerLimit` is
// the contract — future rooms (e.g. a Reserve/Dropped signup room) get
// the same treatment without code here.
const showsUnlimitedTotal = computed<boolean>(() => props.room.noPlayerLimit)

function localizedRowName(row: WeaponStatsRow): string {
  return weaponDisplayName(row.slug, row.name, messages.value.names)
}

function closeStatsPopup(): void {
  statsPopupOpen.value = false
  // `useFocusTrap.deactivate` handles focus restoration back to the trigger
  // (saved on `activate`).
}

function onStatsKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !statsPopupOpen.value) return
  const popupEl = statsPopup.value
  const activeEl = document.activeElement
  if (!popupEl || activeEl === null || !popupEl.contains(activeEl)) return
  event.stopPropagation()
  closeStatsPopup()
}

watch(statsPopupOpen, async (open) => {
  if (open) {
    document.addEventListener('keydown', onStatsKeydown)
    await nextTick()
    activateStatsTrap()
    return
  }
  deactivateStatsTrap()
  document.removeEventListener('keydown', onStatsKeydown)
})

/**
 * Toggle the weapon-stats popup. Clicking the trigger while the popup is
 * open closes it (the outside-click handler treats the trigger as "inside"
 * so it doesn't dismiss on this first click — the toggle below does).
 */
function onStatsChipClick(): void {
  statsPopupOpen.value = !statsPopupOpen.value
}

function onStatsDocClick(event: MouseEvent): void {
  if (!statsPopupOpen.value) return
  const target = event.target as Node | null
  if (!target) return
  const triggerEl = statsTrigger.value
  const popupEl = statsPopup.value
  if (triggerEl && triggerEl.contains(target)) return
  if (popupEl && popupEl.contains(target)) return
  statsPopupOpen.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onStatsDocClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onStatsDocClick)
  document.removeEventListener('keydown', onStatsKeydown)
})
</script>
